import { TextFieldTheme } from '@wix/wix-ui-tpa/cssVars';
import React, { useCallback, useMemo, useState } from 'react';
import { useConfig } from '../../contexts/config-context';
import { useAddressAutofill } from '../../fields/multiline-address/multiline-address/address-autofill-context';
import { useCountryCode } from '../../fields/multiline-address/multiline-address/country-code-context';
import { useFieldAttributes } from '../../services/field-attributes';
import { AutocompleteWUT } from '../autocomplete';
import { createOption } from '../autocomplete/autocomplete-wut';
import { FieldDescriptionWUT } from '../field-description/field-description-wut';
import { FieldErrorWUT } from '../field-error/field-error-wut';
import { FieldLabelWUT } from '../field-label/field-label-wut';
import { useAtlas } from './use-atlas';
import usePlacesAutocomplete from './use-places-autocomplete';
const allowAllOptions = ()=>true;
export const AddressInputWUT = ({ id, target, label, hideLabel, placeholder, required, description, value, onChange, error, hasError = false, errorMessage, disabled, onBlur, onFocus, FieldLayout, requiredIndicatorType, requiredIndicatorPlacement })=>{
    const { labelId, inputId, errorId, descriptionId, ariaDescribedBy } = useFieldAttributes(id, error, description);
    const { instance, language, atlasBaseUrl } = useConfig();
    const { onAddressSelect } = useAddressAutofill();
    const countryCode = useCountryCode();
    const [isAtlasEnabled, setIsAtlasEnabled] = useState(false);
    const atlas = useAtlas({
        enabled: isAtlasEnabled,
        getInstance: ()=>instance ?? '',
        language,
        atlasBaseUrl
    });
    const { predictions, updatePredictions, clearPredictions } = usePlacesAutocomplete({
        atlas
    });
    const options = useMemo(()=>predictions.map((prediction)=>{
            return createOption({
                id: prediction.searchId,
                value: prediction.description
            });
        }), [
        predictions
    ]);
    const handleChange = useCallback((event)=>{
        const newValue = event.target.value;
        const countryCodes = countryCode ? [
            countryCode
        ] : undefined;
        updatePredictions({
            input: newValue,
            countryCodes
        });
        onChange(newValue);
    }, [
        onChange,
        updatePredictions,
        countryCode
    ]);
    const handleSelect = useCallback(async (option)=>{
        clearPredictions();
        if (!atlas) {
            return;
        }
        const address = await atlas.getPlace({
            searchId: option.id
        });
        if (address && onAddressSelect) {
            onAddressSelect(address);
        }
    }, [
        onAddressSelect,
        clearPredictions,
        atlas
    ]);
    const handleManualInput = useCallback(()=>{
        clearPredictions();
    }, [
        clearPredictions
    ]);
    const handleBlur = useCallback((event)=>{
        const newValue = event.target.value;
        clearPredictions();
        onChange(newValue);
        onBlur?.();
    }, [
        onChange,
        onBlur,
        clearPredictions
    ]);
    const handleFocus = useCallback(()=>{
        setIsAtlasEnabled(true);
        onFocus?.();
    }, [
        onFocus
    ]);
    return /*#__PURE__*/ React.createElement(FieldLayout, {
        fieldId: id,
        renderLabel: ()=>/*#__PURE__*/ React.createElement(FieldLabelWUT, {
                labelId: labelId,
                htmlFor: inputId,
                required: required,
                label: label,
                hideLabel: hideLabel,
                requiredIndicatorType: requiredIndicatorType,
                requiredIndicatorPlacement: requiredIndicatorPlacement
            }),
        renderInput: ()=>/*#__PURE__*/ React.createElement(AutocompleteWUT, {
                id: inputId,
                value: value,
                options: options,
                disabled: disabled,
                placeholder: placeholder,
                error: hasError,
                filterPredicate: allowAllOptions,
                onChange: handleChange,
                onSelect: handleSelect,
                onManualInput: handleManualInput,
                onBlur: handleBlur,
                onFocus: handleFocus,
                ariaDescribedBy: ariaDescribedBy,
                theme: TextFieldTheme.Box
            }),
        renderDescription: ()=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(FieldErrorWUT, {
                target: target,
                hasError: hasError,
                errorMessage: errorMessage,
                id: errorId
            }), /*#__PURE__*/ React.createElement(FieldDescriptionWUT, {
                description: description,
                id: descriptionId
            }))
    });
};

//# sourceMappingURL=address-input-wut.js.map